import React from "react";
import Media from "react-media";
import styled, { css } from "styled-components";

import Gettext from "../common/Gettext";
import { Link } from "../common/Link";
import Money from "../Money";

import { IronRedesignThemeProvider } from "../ThemeProvider";
import ClickOutside from "../common/ClickOutside";

import type { Account } from "../../redux/CommonTypes";
import { gaReducer } from "../GA";

// styled
const Flex = styled.div`
  display: flex;
  flex-direction: column;
  flex-wrap: wrap;
  justify-content: center;
  align-content: center;
  align-items: center;
`;

const Overlay = styled(Flex)`
  position: fixed;
  top: 0px;
  left: 0px;
  width: 100%;
  height: 100%;
  padding: 2.5em;
  font-size: 10px;
  line-height: 1;
  background-color: rgba(0, 0, 0, 0.85);
  user-select: none;
  z-index: 20000;
  overflow: auto;

  @media (max-width: 959px) {
    padding: 1.5em;
    font-size: 8px;
  }
  @media (max-width: 567px) {
    font-size: 7px;
  }
`;

const Popup = styled.div`
  position: relative;
  flex-shrink: 1;
  width: 100%;
  max-width: 74em;
  max-height: 100%;
  margin: 0 auto;
  padding: 3.5em 7em 6em;
  text-align: center;
  background-color: #fff;
  border-radius: 0.5em;
  box-shadow: 0 0 17px 0px rgba(0, 0, 0, 1);

  @media (max-width: 959px) {
    max-width: 100%;
    padding: 0 2.8em;
    background-color: transparent;
    box-shadow: none;
  }
`;

const Header = styled(Flex)`
  @media (max-width: 959px) {
    flex: 1 1 100%;
    padding: 0;
  }
`;

const Title = styled.h1`
  font-weight: 200;
  font-size: 3.1em;
  line-height: 1;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  color: #717171;

  @media (max-width: 959px) {
    font-size: 2.4em;
    color: #fd9500;
  }
`;

const Subtitle = styled.p`
  direction: ${({ theme }) => theme.direction};
  padding: 7px 0;
  font-weight: 300;
  font-size: 1.7em;
  line-height: 1.2;
  letter-spacing: 0.6px;
  color: #717171;

  @media (max-width: 959px) {
    padding: 7px 36px 17px;
    font-size: 1.8em;
    line-height: 1.2;
    letter-spacing: 0.13px;
    color: #fff;
  }
  @media (max-width: 959px) and (min-width: 480px) {
    padding: 2px 36px 7px;
  }
`;

const Content = styled(Flex)`
  flex-direction: row;
  justify-content: space-between;
  align-items: flex-start;
  text-align: center;

  @media (min-width: 960px) {
    flex-wrap: nowrap;
  }
`;

const Column = styled(Flex)`
  flex: 1 1 50%;
  max-width: 25em;
  padding-top: 4.5em;
  color: ${props => props.theme.mainFontColor};

  @media (min-width: 960px) {
    max-width: 50%;
  }

  @media (max-width: 959px) {
    flex: 1 1 100%;
    max-width: 100%;
    padding: 1.5em 2.5em;

    :last-of-type {
      margin-bottom: 1.5em;
    }
  }
  @media (max-width: 959px) and (min-width: 480px) {
    padding: 1em 2.5em;
    flex-direction: row;
    justify-content: space-between;
  }

  ${props =>
    props.demo &&
    css`
      color: #fd9500;

      @media (max-width: 959px) {
        background-color: #f6f6f6;
      }
    `} ${props =>
    props.real &&
    css`
      color: #64b410;

      @media (max-width: 959px) {
        background-color: #fff;
      }
    `};
`;

const ColumnHeader = styled(Flex)`
  width: 100%;
  font-weight: 500;

  @media (max-width: 959px) and (min-width: 480px) {
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
  }
`;

const ColumnHeaderItem = styled.p`
  width: inherit;
  font-weight: inherit;

  @media (max-width: 959px) and (min-width: 480px) {
    width: auto;
  }
`;

const ColumnTitle = styled(ColumnHeaderItem)`
  font-size: 2.2em;
  color: ${props => props.theme.mainFontColor};
`;

const ColumnBalance = styled(ColumnHeaderItem)`
  padding-bottom: 0.6em;
  font-size: 3.5em;
  line-height: 1.4;
  color: currentColor;
  border-bottom: 1px solid #e2e2e2;

  @media (max-width: 959px) {
    padding-bottom: 0;
    border-bottom: none;
  }
  @media (max-width: 959px) and (min-width: 480px) {
    flex: 0 1 45%;
    padding-left: 0.6em;
    text-align: center;
  }
`;

const ColumnFeatures = styled.ul`
  padding: 2.2em 0 2.3em 5.7em;
  text-align: left;

  @media (max-width: 959px) {
    padding-top: 0;
  }
  @media (max-width: 959px) and (min-width: 480px) {
    padding-top: 0;
    padding-bottom: 0;
    padding-left: 4em;
    flex: 1 1 45%;
  }
`;

const Feature = styled.li`
  position: relative;
  padding: 0.471em 0;
  font-weight: 300;
  font-size: 1.7em;
  line-height: 1.2;
  letter-spacing: 0.6px;
`;

// prettier-ignore
const FeatureText = styled.span`
  color: ${props => props.theme.mainFontColor};
`;

const Tick = styled.span`
  position: absolute;
  left: -2.235em;
  top: 50%;
  transform: translateY(-50%) rotate(-45deg);
  transform-origin: 25%;
  display: block;
  width: 17px;
  height: 10px;
  border-bottom: 2px solid;
  border-left: 2px solid;
  border-color: currentColor;
`;

const Strong = styled.strong`
  font-weight: 700;
`;

const ColumnButtonContainer = styled.div`
  @media (max-width: 959px) and (min-width: 480px) {
    flex: 0 1 45%;
    padding-left: 2em;
  }
`;

const Button = styled(Flex)`
  width: 100%;
  max-width: 11.364em;
  height: 3.091em;
  margin: 0 auto;
  padding: 0;
  font-weight: 500;
  font-size: 2em;
  letter-spacing: 0.2px;
  text-decoration: none;
  color: #fff;
  border: 0;
  border-radius: 9px;
  cursor: pointer;
`;

const ButtonDemo = styled(Button)`
  background-color: ${props => props.theme.accent};
  background: linear-gradient(${props => props.theme.accent}, #fd9500);

  :hover {
    background-color: #fd9500;
    background: linear-gradient(#fd9500, ${props => props.theme.accent});
  }

  ${props => props.disabled && css`
    background-color: #8a8a8a;
    background: linear-gradient(to bottom, #8a8a8a 0%, #a5a5a5 100%);
    cursor: default;
    :hover {
      background-color: #8a8a8a;
      background: linear-gradient(to bottom, #8a8a8a 0%, #a5a5a5 100%);
    }
  `}
`;

const ButtonReal = styled(Button)`
  background-color: #68c703;
  background: linear-gradient(#68c703, #60a21d);

  :hover {
    background-color: #60a21d;
    background: linear-gradient(#60a21d, #68c703);
  }
`;

const ActionDesc = styled.p`
  direction: ${({ theme }) => theme.direction};
  padding: 0.588em 0;
  font-weight: 300;
  font-size: 1.7em;
  line-height: 1.2;
  letter-spacing: 0.13px;
  color: #717171;
`;

const Close = styled.div`
  position: absolute;
  top: 18px;
  right: 18px;
  width: 18px;
  height: 18px;
  color: #a0a7b2;
  background-size: cover;
  transition: all 0.3s ease;
  cursor: pointer;

  :before,
  :after {
    content: "";
    position: absolute;
    top: -1px;
    left: 50%;
    width: 0px;
    height: 24px;
    border-left: 2px solid;
  }

  :before {
    transform: translateX(-50%) rotate(45deg);
    transform-origin: 100%;
  }

  :after {
    transform: translateX(-50%) rotate(-45deg);
    transform-origin: 0%;
  }

  :hover {
    color: #60a31c;
  }

  @media (max-width: 959px) {
    top: 0;
    width: 26px;
    height: 26px;

    :before,
    :after {
      top: -4px;
      height: 36px;
    }
  }
`;

function Features({ arr }) {
  return arr.map((feat, i) => (
    <Feature key={`feat${i}`}>
      <Tick />
      <FeatureText>{feat}</FeatureText>
    </Feature>
  ));
}

type Props = {
  accounts: Array<Account>,
  initialDeposit: number,
  minFillup: number,
  style?: string,
  onDismiss: () => void,
  onRefillDemoClick: () => void
};

class PopupDeposit extends React.Component<Props> {
  componentDidMount() {
    gaReducer("CLICKBTN_PPP_DEPOSIT_SHOW");
  }

  render() {
    const props = this.props;
    const { accounts, minFillup, initialDeposit } = props;

    const demo = accounts.find(acc => acc.type === "demo");
    const real = accounts.find(acc => acc.type === "real");

    return (
      <Overlay style={props.style}>
        <ClickOutside onOutsideClick={props.onDismiss}>
          {({ innerRef }) => (
            <Popup ref={innerRef}>
              <Close onClick={props.onDismiss} />
              <Header>
                <Title>
                  <Gettext>Make a deposit</Gettext>
                </Title>
                <Subtitle>
                  <Gettext>
                    You can switch between your accounts at any moment!
                  </Gettext>
                </Subtitle>
              </Header>
              <Content>
                <Column demo>
                  <ColumnHeader>
                    <ColumnTitle>
                      <Media query="(max-width: 959px)">
                        {matches =>
                          matches ? (
                            <Gettext>Fill Up Demo Account</Gettext>
                          ) : (
                            <Gettext>Demo account</Gettext>
                          )
                        }
                      </Media>
                    </ColumnTitle>
                    <ColumnBalance>
                      <Money>{demo ? demo.balance : 0}</Money>
                    </ColumnBalance>
                  </ColumnHeader>
                  <ColumnFeatures>
                    <Features
                      arr={[
                        <Strong>
                          <Gettext>No withdrawals</Gettext>
                        </Strong>,
                        <Gettext>All assets are available</Gettext>,
                        <Gettext>Full-fledged platform</Gettext>
                      ]}
                    />
                  </ColumnFeatures>
                  <ColumnButtonContainer>
                    <IronRedesignThemeProvider>
                    <ButtonDemo
                      as="button"
                      disabled={props.isFetching}
                      onClick={() => {
                        props.onRefillDemoClick().then(props.onDismiss)
                          .then(() => gaReducer("CLICKBTN_PPP_DEPOSIT_REFILL"));
                      }}
                    >
                      <Gettext>Practice trading</Gettext>
                    </ButtonDemo>
                    </IronRedesignThemeProvider>
                    <ActionDesc>
                      <Gettext>Free Refill with</Gettext>{" "}
                      <Money>{initialDeposit}</Money>
                    </ActionDesc>
                  </ColumnButtonContainer>
                </Column>
                <Column real>
                  <ColumnHeader>
                    <ColumnTitle>
                      <Media query="(max-width: 959px)">
                        {matches =>
                          matches ? (
                            <Gettext>Fill Up Real Account</Gettext>
                          ) : (
                            <Gettext>Real account</Gettext>
                          )
                        }
                      </Media>
                    </ColumnTitle>
                    <ColumnBalance>
                      <Money>{real ? real.balance : 0}</Money>
                    </ColumnBalance>
                  </ColumnHeader>
                  <ColumnFeatures>
                    <Features
                      arr={[
                        <Strong>
                          <Gettext>Easy withdrawals</Gettext>
                        </Strong>,
                        <Gettext>All assets are available</Gettext>,
                        <Gettext>Full-fledged platform</Gettext>
                      ]}
                    />
                  </ColumnFeatures>
                  <ColumnButtonContainer>
                    <ButtonReal
                      as={Link}
                      to="/fillup"
                      onClick={() => {
                        gaReducer("CLICKBTN_PPP_DEPOSIT_FILLUP");
                        props.onDismiss();
                      }}
                    >
                      <Gettext>Add Money</Gettext>
                    </ButtonReal>
                    <ActionDesc>
                      <Gettext>Minimum deposit is</Gettext>{" "}
                      <Money>{minFillup}</Money>
                    </ActionDesc>
                  </ColumnButtonContainer>
                </Column>
              </Content>
            </Popup>
          )}
        </ClickOutside>
      </Overlay>

    )
  }
}
export default PopupDeposit;



// WEBPACK FOOTER //
// ./src/components/PopupDeposit/PopupDeposit.js